import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { FileTooLargeError } from 'reactApp/modules/uploading/errors/FileTooLargeError';
import { ConnectionFail } from 'reactApp/modules/uploading/fails/ConnectionFail';
import { FileExistsFail } from 'reactApp/modules/uploading/fails/FileExistsFail';
import { OverQuotaFail } from 'reactApp/modules/uploading/fails/OverQuotaFail';
import { PublicFileSizeLimit } from 'reactApp/modules/uploading/fails/PublicFileSizeLimit';
import { ReadOnlyDirectoryFail } from 'reactApp/modules/uploading/fails/ReadOnlyDirectoryFail';
import { RetryUploadFileFail } from 'reactApp/modules/uploading/fails/RetryUploadFileFail';
import { UserFileSizeLimitFail } from 'reactApp/modules/uploading/fails/UserFileSizeLimitFail';
import { ViolatedFilenameFail } from 'reactApp/modules/uploading/fails/ViolatedFilenameFail';
import { handleFileExistsError } from 'reactApp/modules/uploading/sagas/handleErrors/handleFileExistsError';
import { handleFileSizeLimitModal } from 'reactApp/modules/uploading/sagas/handleErrors/handleFileSizeLimitModal';
import { handleOverQuotaError } from 'reactApp/modules/uploading/sagas/handleErrors/handleOverQuotaError';
import { handleReadOnlyDirectory } from 'reactApp/modules/uploading/sagas/handleErrors/handleReadOnlyDirectory';
import { UploadingDescriptor } from 'reactApp/modules/uploading/serviceClasses/UploadingDescriptor';
import { uploadingLog } from 'reactApp/modules/uploading/serviceClasses/UploadingLog';
import { uploadingService } from 'reactApp/modules/uploading/serviceClasses/UploadingService';
import { ERetryErrorFileOptions, EUploadingState } from 'reactApp/modules/uploading/uploading.types';
import { EFileError, EFileStatus } from 'reactApp/modules/uploadList/uploadList.model';
import { setUserFileSizeLimit, updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { call, put, select } from 'redux-saga/effects';

import { handleViolatedFileNameError } from './handleErrors/handleViolatedFileNameError';

export function* processFileUploadError(error: Error, descriptor: UploadingDescriptor) {
    const isPaidUser = yield select(UserSelectors.isPaidUser);
    const isCorp = yield select(UserSelectors.isCorpUser);

    const isExeption = error instanceof Error;
    const isConnectionFail = error instanceof ConnectionFail;
    const isGatewayFail = isConnectionFail && error.isGatewayProblem();
    const isOverQuotaFail = error instanceof OverQuotaFail;
    const isFileExistsFail = error instanceof FileExistsFail;
    const isUserFileSizeLimitFail = error instanceof UserFileSizeLimitFail;
    const isFileTooLargeError = error instanceof FileTooLargeError;
    const isReadOnlyDirectoryFail = error instanceof ReadOnlyDirectoryFail;
    const isPublicFileSizeLimit = error instanceof PublicFileSizeLimit;
    const isViolatedFilenameFail = error instanceof ViolatedFilenameFail;
    const cloudPath = descriptor.cloudPath;
    const descriptorId = descriptor.id;

    let retryResult: ERetryErrorFileOptions = ERetryErrorFileOptions.cancel;

    if (error instanceof RetryUploadFileFail) {
        return ERetryErrorFileOptions.shouldRetry;
    }

    if (isUserFileSizeLimitFail) {
        yield put(setUserFileSizeLimit(error.limit));
    }

    const isUserFileSizeLimit =
        isUserFileSizeLimitFail && !isOverQuotaFail && !(descriptor.uploadingPacketConfig.storage === EStorageType.public || isCorp);

    const isOverQuota =
        (isPublicFileSizeLimit && descriptor.uploadingPacketConfig.isOwnPublic) ||
        isOverQuotaFail ||
        ((isFileTooLargeError || isUserFileSizeLimitFail) && !isPaidUser);

    if (isExeption) {
        // Остальные ошибки уже залогировал descriptor.
        uploadingLog.error(error);
    }

    if (isPublicFileSizeLimit && !descriptor.uploadingPacketConfig.isOwnPublic) {
        yield put(
            updateUploadFilesAction({
                descriptorId,
                cloudPath,
                status: EFileStatus.ERROR,
                error: EFileError.OVER_QUOTA_CLOUD_AT_OWNER,
            })
        );
    }

    if (isUserFileSizeLimit) {
        const filesOverLimit = uploadingService.getDescriptorsOverLimit(descriptor.uploadingPacketConfig.userFileSizeLimit);
        retryResult = yield call(handleFileSizeLimitModal, { descriptor, filesOverLimit });
    } else if (isOverQuota) {
        retryResult = yield call(handleOverQuotaError, descriptor);
    } else if (isFileTooLargeError || isUserFileSizeLimitFail) {
        descriptor.failPaused();
        yield put(
            updateUploadFilesAction({
                descriptorId,
                cloudPath,
                status: EFileStatus.ERROR,
                error: EFileError.USER_FILE_SIZE_LIMIT,
            })
        );
    } else if (isFileExistsFail) {
        retryResult = yield call(handleFileExistsError, descriptor);
    } else if (isReadOnlyDirectoryFail) {
        retryResult = yield call(handleReadOnlyDirectory, descriptor);
    } else if (descriptor.state === EUploadingState.STATE_CANCEL) {
        yield put(
            showSnackbarAction({
                text: 'Загрузка отменена',
                id: 'uploaderror',
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
    } else if (isViolatedFilenameFail) {
        retryResult = yield call(handleViolatedFileNameError, descriptor);
    } else if (!isGatewayFail) {
        yield put(
            showSnackbarAction({
                text: 'Ошибка загрузки',
                id: 'uploaderror',
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
    }

    if (isConnectionFail) {
        retryResult = ERetryErrorFileOptions.connectionErrorRetry;
    }

    return retryResult;
}
